import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents } from '@shopify/events';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble
 * @property {HTMLElement} cartBubbleText
 * @property {HTMLElement} cartBubbleCount
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  countRequestId = 0;
  countController = null;

  get currentCartCount() {
    return Number.parseInt(
      this.refs.cartBubbleCount.textContent || '0',
      10
    ) || 0;
  }

  set currentCartCount(value) {
    this.refs.cartBubbleCount.textContent =
      value < 100 ? String(value) : '';
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.addEventListener('pageshow', this.onPageShow);

    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.onCartUpdate
    );

    window.removeEventListener('pageshow', this.onPageShow);

    this.countRequestId += 1;
    this.countController?.abort();
  }

  onPageShow = (event) => {
    if (event.persisted) {
      this.ensureCartBubbleIsCorrect();
    }
  };

  onCartUpdate = (event) => {
    void this.refreshCartCount(true, event.promise);
  };

  refreshCartCount = async (animate = false, updatePromise) => {
    const requestId = ++this.countRequestId;

    this.countController?.abort();

    const controller = new AbortController();
    this.countController = controller;

    try {
      if (updatePromise) {
        await updatePromise;
      }

      if (
        requestId !== this.countRequestId ||
        !this.isConnected
      ) {
        return;
      }

      const root = window.Shopify?.routes?.root || '/';

      const response = await fetch(`${root}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: {
          Accept: 'application/json'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        throw new Error('Unable to retrieve cart count.');
      }

      const cart = await response.json();

      if (!Array.isArray(cart.items)) {
        throw new Error('Invalid cart response.');
      }

      if (
        requestId !== this.countRequestId ||
        !this.isConnected
      ) {
        return;
      }

      const itemCount = cart.items.reduce((total, item) => {
        const isProtection =
          Number(item.product_id) === 7298841280670 ||
          item.handle === 'easy-insurance';

        return isProtection
          ? total
          : total + Number(item.quantity || 0);
      }, 0);

      await this.renderCartBubble(itemCount, animate);
    } catch (error) {
      if (
        error?.name !== 'AbortError' &&
        requestId === this.countRequestId
      ) {
        console.warn('[cart-icon] Count update failed:', error);
      }
    }
  };

  renderCartBubble = async (itemCount, animate = true) => {
    this.refs.cartBubbleCount.classList.toggle(
      'hidden',
      itemCount === 0
    );

    this.refs.cartBubble.classList.toggle(
      'visually-hidden',
      itemCount === 0
    );

    this.refs.cartBubble.toggleAttribute(
      'data-maintain-ratio',
      itemCount <= 99
    );

    this.currentCartCount = itemCount;

    this.classList.toggle(
      'header-actions__cart-icon--has-cart',
      itemCount > 0
    );

    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: String(itemCount),
          timestamp: Date.now()
        })
      );
    } catch {}

    if (!animate || itemCount === 0) return;

    await new Promise(resolve => requestAnimationFrame(resolve));

    if (!this.isConnected) return;

    this.refs.cartBubble.classList.add('cart-bubble--animating');

    await onAnimationEnd(this.refs.cartBubbleText);

    if (!this.isConnected) return;

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  ensureCartBubbleIsCorrect = () => {
    if (!this.refs.cartBubbleCount) return;

    void this.refreshCartCount(false);
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}